import React, { useEffect, useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { TranslateFunction } from 'react-localize-redux';

import {
  PriceTag,
  SectionItem,
  Typography,
} from '../../../../../components/atoms';
import { Currency } from '../../../../../core/api/general/general.types';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
  PickupLocationType,
} from '../../../../../core/api/restaurant/restaurant.types';
import { Routes } from '../../../../../core/types/route.types';
import { Track } from '../../../../../weborder/analytics';
import * as BasketControl from '../../../../../weborder/BasketControl';
import { OrderPayload } from '../../../../../weborder/selectors/basketSelectors';
import { usePrevious } from '../../../../../weborder/utils';
import { SettingEditor } from '../../../components/SettingEditor';
import { checkoutUtils } from '../../../utils';

type Props = {
  basket: BasketControl.Data;
  currency: Currency;
  currentLang: string;
  orderPayload: OrderPayload;
  params: Routes['params'];
  pickupLocationType: PickupLocationType;
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail;
  tableNumber: number;
  toggleVoucherDialog: () => void;
  translate: TranslateFunction;
};

type VoucherData = {
  amount: number;
  description: string;
  name: string;
  taxAmount: number;
  totalAmount: number;
};

const useStyles = () =>
  makeStyles((theme: Theme) => ({
    descriptionText: {
      margin: theme.spacing(1, 0),
    },
    priceTag: {
      minHeight: 0,
      whiteSpace: 'nowrap',
    },
  }))();

export function VoucherSetting(props: Props): JSX.Element {
  const {
    basket,
    currency,
    currentLang,
    orderPayload,
    params,
    pickupLocationType,
    restaurant,
    tableNumber,
    toggleVoucherDialog,
    translate,
  } = props;

  const { type, vrid, id } = params;

  const isMissingPreOrderTime =
    typeof orderPayload === 'string' &&
    orderPayload === 'missing_pre_order_time';
  const isMissingTableNumber =
    typeof tableNumber !== 'number' &&
    checkoutUtils.getIsTableSelectOnly(restaurant, pickupLocationType);
  const isVoucherDisabled = isMissingPreOrderTime || isMissingTableNumber;

  const [voucherData, setVoucherData] = useState<VoucherData>({
    amount: 1,
    description: '',
    name: '',
    taxAmount: 0,
    totalAmount: 0,
  });

  const previousVoucherData = usePrevious(voucherData);

  const classes = useStyles();

  useEffect(() => {
    if (basket.voucher.status === 'valid') {
      const [firstVoucher] = basket.voucher.items;
      const { Amount, Description, Name, TaxAmount } = firstVoucher;
      const hasAmountChanged =
        !previousVoucherData || Amount !== previousVoucherData.amount;

      if (hasAmountChanged) {
        const newVoucherData = {
          amount: Amount,
          description: Description,
          name: Name,
          taxAmount: TaxAmount,
          totalAmount: Amount + TaxAmount,
        };
        setVoucherData(newVoucherData);
      }
    }
  }, [basket.voucher.status]);

  const getHelperText = (): string => {
    let result;

    if (isMissingPreOrderTime) {
      result = translate('Please_select_a_time_before_adding_voucher', {
        type: translate(type) as string,
      }) as string;
    }

    if (isMissingTableNumber) {
      const tableServiceCategory =
        checkoutUtils.getTableServiceCategory(restaurant);
      result = translate(`Table_voucher_${tableServiceCategory}`) as string;
    }
    return result;
  };

  const handleOnClick = (): void => {
    Track('Orders', 'Add Voucher');
    toggleVoucherDialog();
  };

  if (!type || !vrid || !id) {
    return null;
  }
  const isLoading =
    basket.voucher.status === 'loading' && voucherData.amount !== 1;
  const helperText = getHelperText();
  const setting = voucherData.amount !== 1 && (
    <>
      <SectionItem
        dense
        label={`${translate('Voucher')} ${voucherData.name}`}
        rightHeader={
          <PriceTag
            className={classes.priceTag}
            data-fd="voucher-value"
            hasMenuOptions={false}
            localisedPrice={BasketControl.ToCurrency(
              voucherData.totalAmount,
              currency,
              currentLang,
            )}
            translate={translate}
          />
        }
      />
      {voucherData.description && (
        <Typography
          className={classes.descriptionText}
          variant="body2-secondary"
        >
          {voucherData.description}
        </Typography>
      )}
    </>
  );

  return (
    <SettingEditor
      addLabel={translate('Add_voucher')}
      changeLabel={translate('Change_voucher')}
      fdKey="voucher"
      handleOnAdd={handleOnClick}
      handleOnChange={handleOnClick}
      helperText={helperText}
      isDisabled={isVoucherDisabled}
      isLoading={isLoading}
      setting={setting}
    />
  );
}
